/*
 *  Copyright (C) 2019 Impulsis <info@impulsis.com>. All rights reserved.
 *
 *  All information herein, source code below as well as the Morpheus system itself and all related
 *  software (“Items”) are proprietary to Impulsis. Impulsis is the sole copyright owner of the Items.
 *  Any use, reproduction, or disclosure of the Items without the written permission of Impulsis
 *  is prohibited.
 */

import { AxiosInstance } from 'axios'
import { RootState } from '@/store/types'
import { Store } from 'vuex'
import router from '@/router/router'
import { refreshToken } from '@morpheus/admin/admin-vue/services/auth-client'
import { parseJwt } from '@morpheus/utils/admin-vue/helpers/parseJwt'

export function attachAccessTokenToHeaders (axiosInstance: AxiosInstance, store: Store<RootState>) {
  axiosInstance.interceptors.request.use((config) => {
    const token: string = store.getters['auth/accessToken']
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })
}

export function autoLogoutUser (axiosInstance: AxiosInstance, store: Store<RootState>) {
  axiosInstance.interceptors.response.use((response) => {
    const jwtIatTimestamp = store.getters['auth/jwtIatTimestamp']
    const jwtExpTimestamp = store.getters['auth/jwtExpTimestamp']
    const now: number = (new Date()).getTime()
    // check if token refresh is needed
    if (jwtExpTimestamp && jwtExpTimestamp - now / 1000 < (jwtExpTimestamp - jwtIatTimestamp) / 2) {
      refreshToken()
        .then(response => {
          const token = response.token
          const jwtIatTimestamp = parseJwt(token).iat
          const jwtExpTimestamp = parseJwt(token).exp

          store.commit('auth/refreshToken', { token: token, jwtIatTimestamp: jwtIatTimestamp, jwtExpTimestamp: jwtExpTimestamp })
        })
    }

    return response
  }, (error) => {
    if (!error.response) {
      // IMPORTANT NOTE: actually this was intended to handle specifically 502, 503, 504 status codes generated by balancer,
      // BUT since all requests are cross-origin, they break at preceding OPTIONS and we are unable to guarantee proper status here
      // hence this will really trigger upon *any* error status in response to OPTIONS, even non 5xx

      /* eslint-disable-next-line prefer-promise-reject-errors */
      return Promise.reject({ response: { data: { error: error.message } } })
    }

    if ('status' in error.response && error.response.status === 401) {
      store.commit('auth/logout')
      if (router.currentRoute.name !== 'login') {
        router.push({ name: 'login' })
      }
    }

    return Promise.reject(error)
  })
}

export default {
  attachAccessTokenToHeaders,
  autoLogoutUser
}
